import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { payslipWarnings, isSendablePhone } from '../utils/payrollParse';
import { buildPayslipDoc, loadBcplLogo } from '../utils/payslipPDF';
import { planPaymentModeExport, writePaymentModeWorkbook, rupees, PAYMENT_MODES, ATTENTION_SHEET } from '../utils/payrollExport';
import { RefreshCw, AlertTriangle, FileText, Send, FileDown, FileSpreadsheet } from 'lucide-react';

// Stage 3 — review what is IN THE DATABASE for a month: the stored payslip
// rows, PDF and send state per employee, and warnings recomputed from stored
// data (the upload preview validates a file; this validates the truth).
// Read-only by design: nothing generates or sends from this screen.

const card = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 20,
};

const inr = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const fmtMonth = (ym) => {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return ym || '';
  const [y, m] = ym.split('-').map(Number);
  return `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]} ${y}`;
};

// DD-MM-YYYY, the app-wide display convention.
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

// Total deductions as the slip shows them: statutory + the leave deduction
// (NULL on pre-attendance slips). Gross − this = net pay on attendance slips.
const deductions = (s) => (s.pf ?? 0) + (s.esic ?? 0) + (s.professional_tax ?? 0) + (s.lwp_deduction ?? 0);

// "31 / 2" days paid/unpaid; em dash on pre-attendance slips.
const daysCell = (s) => (s.days_paid === null || s.days_paid === undefined ? '—' : `${s.days_paid} / ${s.days_unpaid ?? 0}`);

const statLabel = { fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const statValue = { fontSize: '1.25rem', fontWeight: 700, color: NAVY, fontVariantNumeric: 'tabular-nums' };

export default function PayrollReview({ initialMonth }) {
  const [months, setMonths] = useState(null); // null = loading
  const [month, setMonth] = useState(initialMonth || '');
  const [slips, setSlips] = useState(null);
  // No-slip attendance rows for the month: calculated pay, deliberately no
  // payslip. Two kinds, told apart by status — 'blocked' (mid-month joiners,
  // mismatches: a data problem to fix) and 'excluded' (payment type Cash:
  // no slip BY DESIGN, 08-09-2026). Both count in the month totals; no PDF,
  // no send — the send flow never reads payroll_attendance.
  // A load failure here is NEVER silent: it renders its own card in the
  // section's place, because "no section" must always mean "no no-slip rows",
  // never "the query failed and the totals are quietly short".
  const [noSlip, setNoSlip] = useState([]);
  const [noSlipError, setNoSlipError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null); // { done, total }
  const [genErrors, setGenErrors] = useState([]);
  const [confirm, setConfirm] = useState(null); // { mode: 'single'|'batch', slip? }
  const [sending, setSending] = useState(null); // { done, total }
  const [sendFailures, setSendFailures] = useState([]); // { id, label, error }
  const [sendNotice, setSendNotice] = useState('');
  const stopRef = useRef(false);

  // Export by payment mode: the plan is built from a FRESH read of the month
  // (payslips + no-slip attendance + their employees), shown for approval
  // with the month and total stated plainly, then rendered on Download.
  const [exportPlan, setExportPlan] = useState(null); // null | { plan } | { error }
  const [exportBusy, setExportBusy] = useState(false);

  // Per-employee history (Stage 6): set from a click on an employee row.
  const [historyEmp, setHistoryEmp] = useState(null); // employees row
  const [historySlips, setHistorySlips] = useState(null); // null = loading

  const loadMonths = useCallback(async () => {
    setError('');
    const { data, error: err } = await supabase.from('payslips').select('month');
    if (err) { setError(`Could not load payslip months: ${err.message}`); setMonths([]); return; }
    const distinct = [...new Set((data || []).map((r) => r.month))].sort().reverse();
    setMonths(distinct);
    setMonth((cur) => (cur && distinct.includes(cur) ? cur : (distinct[0] || '')));
  }, []);

  useEffect(() => { loadMonths(); }, [loadMonths]);

  const loadSlips = useCallback(async (m) => {
    if (!m) { setSlips([]); return; }
    setLoading(true);
    setError('');
    const [{ data, error: err }, attRes] = await Promise.all([
      supabase
        .from('payslips')
        .select('id, month, gross, basic, da, residual_pay, pf, esic, professional_tax, days_paid, days_unpaid, daily_wage, lwp_deduction, gross_earned, net_pay, pdf_path, sent_at, send_status, updated_at, employees(id, employee_code, name, department, phone, uan, payment_type)')
        .eq('month', m),
      // No employees(...) embed: muster_name / muster_employee_id are stored
      // on the row itself, so the join buys nothing and adds a failure mode.
      supabase
        .from('payroll_attendance')
        .select('id, muster_employee_id, muster_name, status, dash_days, days_paid, days_unpaid, daily_wage, unpaid_deduction, gross, pf, esic, professional_tax, gross_earned, net_pay, flags')
        .eq('month', m)
        .in('status', ['blocked', 'excluded']),
    ]);
    setLoading(false);
    if (err) { setError(`Could not load payslips: ${err.message}`); setSlips([]); setNoSlip([]); setNoSlipError(''); return; }
    if (attRes.error) {
      setNoSlip([]);
      setNoSlipError(`No-slip attendance rows (blocked and Cash) could not be loaded — if any employee is blocked or paid in Cash this month, the totals above are MISSING their pay. ${attRes.error.message}`);
    } else {
      const att = [...(attRes.data || [])];
      // Blocked first (they need action), then Cash; each by employee code.
      att.sort((a, b) => (a.status === 'blocked' ? 0 : 1) - (b.status === 'blocked' ? 0 : 1)
        || String(a.muster_employee_id).localeCompare(String(b.muster_employee_id)));
      setNoSlip(att);
      setNoSlipError('');
    }
    const rows = (data || []).map((s) => ({ ...s, warnings: payslipWarnings(s) }));
    rows.sort((a, b) => String(a.employees?.employee_code || '').localeCompare(String(b.employees?.employee_code || '')));
    setSlips(rows);
  }, []);

  useEffect(() => { if (month) loadSlips(month); }, [month, loadSlips]);

  // All months for one employee, newest first. Same row shape as the month
  // view (employees embedded), so View / Send / confirm reuse the same paths.
  const loadHistory = useCallback(async (empId) => {
    setHistorySlips(null);
    setError('');
    const { data, error: err } = await supabase
      .from('payslips')
      .select('id, month, gross, basic, da, residual_pay, pf, esic, professional_tax, days_paid, days_unpaid, daily_wage, lwp_deduction, gross_earned, net_pay, pdf_path, sent_at, send_status, updated_at, employees(id, employee_code, name, department, phone, uan, payment_type)')
      .eq('employee_id', empId);
    if (err) { setError(`Could not load payslip history: ${err.message}`); setHistorySlips([]); return; }
    const rows = (data || []).map((s) => ({ ...s, warnings: payslipWarnings(s) }));
    rows.sort((a, b) => String(b.month).localeCompare(String(a.month)));
    setHistorySlips(rows);
  }, []);

  const openHistory = (emp) => {
    if (!emp?.id) return;
    setHistoryEmp(emp);
    loadHistory(emp.id);
  };

  // Generate PDFs for this month — 'missing' (no pdf_path yet) or 'all'
  // (regenerate, e.g. after a re-upload changed figures). Sequential on
  // purpose: 52 uploads in parallel would hammer storage for no real gain.
  const handleGenerate = async (mode) => {
    if (generating || !slips) return;
    // Stale Cash slips are inert: no PDF is ever produced for them.
    const targets = slips.filter((s) => (mode === 'all' || !s.pdf_path) && s.employees && !isCashSlip(s));
    if (!targets.length) return;
    setGenerating(true);
    setError('');
    setGenErrors([]);
    setGenProgress({ done: 0, total: targets.length });
    const errors = [];
    // The details block is printed from the employee records as they are
    // stored right now, read afresh: the list on screen does not carry
    // account numbers, and HR may have just filled in an ESIC number or IFSC
    // on the Employees tab. A failed read refuses the whole run — a slip
    // printed with "NA" for details that do exist is worse than no slip.
    const empIds = [...new Set(targets.map((s) => s.employees.id))];
    const { data: emps, error: empErr } = await supabase
      .from('employees')
      .select('id, employee_code, name, designation, department, uan, esic_number, payment_type, account_number, ifsc_code')
      .in('id', empIds);
    if (empErr) {
      setError(/column|schema cache/i.test(empErr.message || '')
        ? `No PDFs were generated: the employee table is missing the new columns. Paste supabase/manual/2026-10-02_employees_designation_esic_number_ifsc.sql into the Supabase SQL editor, then try again. (${empErr.message})`
        : `No PDFs were generated: employee records could not be read. ${empErr.message}`);
      setGenerating(false);
      setGenProgress(null);
      return;
    }
    const empById = new Map((emps || []).map((e) => [e.id, e]));
    const logo = await loadBcplLogo();
    if (!logo) errors.push('Logo failed to load — slips were generated without it.');
    for (const slip of targets) {
      const label = `${slip.employees.name} (${slip.employees.employee_code})`;
      try {
        const emp = empById.get(slip.employees.id);
        if (!emp) throw new Error('employee record could not be read');
        const doc = buildPayslipDoc({ ...slip, employees: emp }, logo);
        const blob = doc.output('blob');
        const path = `${slip.month}/${slip.employees.employee_code}.pdf`;
        const { error: upErr } = await supabase.storage
          .from('payslips')
          .upload(path, blob, { upsert: true, contentType: 'application/pdf' });
        if (upErr) throw upErr;
        const { error: rowErr } = await supabase
          .from('payslips').update({ pdf_path: path }).eq('id', slip.id);
        if (rowErr) throw rowErr;
      } catch (err) {
        errors.push(`${label}: ${err.message || err}`);
      }
      setGenProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setGenErrors(errors);
    setGenerating(false);
    setGenProgress(null);
    loadSlips(month);
  };

  // Open a stored PDF via a short-lived signed URL. The tab is opened inside
  // the click gesture, THEN pointed at the URL, so popup blockers stay quiet.
  const handleView = async (slip) => {
    const w = window.open('', '_blank');
    const { data, error: err } = await supabase.storage
      .from('payslips').createSignedUrl(slip.pdf_path, 60);
    if (err || !data?.signedUrl) {
      if (w) w.close();
      setError(`Could not open the PDF: ${err?.message || 'no signed URL'}`);
      return;
    }
    if (w) w.location = data.signedUrl;
  };

  // Export by payment mode — bank transfer / cash disbursement instruction.
  // Reads the month afresh rather than reusing screen state: the no-slip rows
  // on screen carry no account numbers, and a transfer file must be built
  // from exactly what is stored right now. Any query failure refuses the
  // whole export — a partial bank file is worse than none.
  const prepareExport = async () => {
    if (!month || exportBusy) return;
    setExportBusy(true);
    setExportPlan(null);
    try {
      const [slipsRes, attRes] = await Promise.all([
        supabase.from('payslips')
          .select('id, employee_id, net_pay, employees(id, employee_code, name, payment_type, account_number, ifsc_code)')
          .eq('month', month),
        supabase.from('payroll_attendance')
          .select('id, employee_id, muster_employee_id, muster_name, status, net_pay, flags')
          .eq('month', month)
          .in('status', ['blocked', 'excluded']),
      ]);
      if (slipsRes.error) throw new Error(`Payslips could not be read: ${slipsRes.error.message}`);
      if (attRes.error) throw new Error(`No-slip attendance rows could not be read: ${attRes.error.message}`);
      const ids = [...new Set((attRes.data || []).map((a) => a.employee_id).filter(Boolean))];
      let emps = [];
      if (ids.length) {
        const empRes = await supabase.from('employees').select('id, employee_code, name, payment_type, account_number, ifsc_code').in('id', ids);
        if (empRes.error) throw new Error(`Employee records could not be read: ${empRes.error.message}`);
        emps = empRes.data || [];
      }
      const plan = planPaymentModeExport({ month, slips: slipsRes.data || [], attendance: attRes.data || [], employees: emps });
      if (!plan.people) throw new Error(`Nothing is stored for ${fmtMonth(month)} — no payslips and no calculated attendance. Upload the month first.`);
      setExportPlan({ plan });
    } catch (err) {
      setExportPlan({ error: `Export refused — nothing was written. ${err.message || err}` });
    } finally {
      setExportBusy(false);
    }
  };

  const downloadExport = async () => {
    const plan = exportPlan?.plan;
    if (!plan || exportBusy) return;
    setExportBusy(true);
    try {
      const buffer = await writePaymentModeWorkbook(plan);
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = plan.filename;
      link.click();
      URL.revokeObjectURL(url);
      setExportPlan(null);
    } catch (err) {
      setExportPlan({ error: `The workbook could not be written: ${err.message || err}` });
    } finally {
      setExportBusy(false);
    }
  };

  // Cash employees get NO payslip row (08-09-2026): their pay is stored on
  // payroll_attendance, so there is nothing here to PDF or send. A payslip
  // row for a Cash employee can therefore only be STALE — stored before the
  // change, or the payment type was switched to Cash afterwards. It is
  // labelled as such and never sent (the edge function refuses it too);
  // re-uploading the month deletes it and moves the pay to attendance.
  const isCashSlip = (s) => String(s.employees?.payment_type || '').trim().toLowerCase() === 'cash';
  const STALE_CASH = 'Cash employee — payslips are no longer issued; re-upload the month to remove this stale row';

  // Why a row cannot be sent, or null if it can. Mirrors the edge function's
  // server-side checks — the browser gates for UX, the function is authority.
  const sendBlockReason = (s) => {
    if (isCashSlip(s)) return STALE_CASH;
    if (!isSendablePhone(s.employees?.phone)) return 'Phone number missing or invalid — cannot send';
    if (!s.pdf_path) return 'No PDF — generate first';
    return null;
  };

  // Shared cell renderers — the month table and the per-employee history table
  // use the SAME View (signed URL) and Send/Re-send (confirm -> runSend) paths.
  const pdfCell = (s) => (
    s.pdf_path ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span className="badge badge-green"><FileText size={11} style={{ marginRight: 4, verticalAlign: -1 }} aria-hidden="true" />Generated</span>
        <button
          type="button"
          onClick={() => handleView(s)}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent-strong)', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
        >
          View
        </button>
      </span>
    ) : <span style={{ color: 'var(--color-ink-faint)' }}>—</span>
  );

  const sentCell = (s) => {
    // Stale Cash slip: not "Pending" (nothing is ever coming) — a plain badge
    // saying what to do. No Send button at all.
    if (isCashSlip(s) && s.send_status !== 'sent') {
      return (
        <span className="badge badge-amber" title={STALE_CASH}>
          Cash — stale slip
        </span>
      );
    }
    return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {s.send_status === 'sent'
        ? <span className="badge badge-green"><Send size={11} style={{ marginRight: 4, verticalAlign: -1 }} aria-hidden="true" />Sent {fmtDate(s.sent_at)}</span>
        : s.send_status === 'failed'
          ? <span className="badge badge-red">Failed</span>
          : <span className="badge badge-amber">Pending</span>}
      {sendBlockReason(s) ? (
        <button
          type="button"
          disabled
          title={sendBlockReason(s)}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-ink-faint)', fontSize: '0.78rem', fontWeight: 700, cursor: 'not-allowed' }}
        >
          Send
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setConfirm({ mode: 'single', slip: s })}
          disabled={!!sending || generating}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-accent-strong)', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
        >
          {s.send_status === 'sent' ? 'Re-send' : 'Send'}
        </button>
      )}
    </span>
    );
  };

  const CHUNK = 10;

  // Send the given payslip ids in chunks. The Stop button halts before the
  // next chunk — no rollback, anything already sent is sent.
  const runSend = async (ids) => {
    const labelById = new Map([...(slips || []), ...(historySlips || [])]
      .map((s) => [s.id, `${s.employees?.name} (${s.employees?.employee_code})`]));
    stopRef.current = false;
    setSending({ done: 0, total: ids.length });
    setSendFailures([]);
    setSendNotice('');
    const failures = [];
    let done = 0;
    let stopped = false;

    for (let i = 0; i < ids.length; i += CHUNK) {
      if (stopRef.current) { stopped = true; break; }
      const chunk = ids.slice(i, i + CHUNK);
      try {
        const { data, error: fnErr } = await supabase.functions.invoke('send-payslip', {
          body: { payslipIds: chunk },
        });
        if (fnErr) throw fnErr;
        for (const r of data?.results || []) {
          if (!r.success) failures.push({ id: r.id, label: labelById.get(r.id) || r.id, error: r.error || 'Unknown error' });
          else if (r.error) failures.push({ id: r.id, label: labelById.get(r.id) || r.id, error: r.error });
        }
      } catch (err) {
        for (const id of chunk) failures.push({ id, label: labelById.get(id) || id, error: err.message || String(err) });
      }
      done += chunk.length;
      setSending({ done, total: ids.length });
    }

    setSending(null);
    setSendFailures(failures.filter((f) => !String(f.error).startsWith('Sent, but')));
    const recordIssues = failures.filter((f) => String(f.error).startsWith('Sent, but'));
    const sentCount = done - failures.filter((f) => !String(f.error).startsWith('Sent, but')).length;
    setSendNotice(
      stopped
        ? `Stopped after ${done} of ${ids.length}. Messages already sent are sent; the remaining ${ids.length - done} were not attempted.`
        : `Finished: ${sentCount} sent, ${failures.length - recordIssues.length} failed${recordIssues.length ? `, ${recordIssues.length} sent but not recorded` : ''}.`,
    );
    loadSlips(month);
    if (historyEmp) loadHistory(historyEmp.id);
  };

  const handleConfirmSend = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.mode === 'single') await runSend([c.slip.id]);
    else await runSend(c.targets.map((s) => s.id));
  };

  const openBatchConfirm = () => {
    const targets = (slips || []).filter((s) => s.send_status !== 'sent' && !sendBlockReason(s));
    const skippedSent = (slips || []).filter((s) => s.send_status === 'sent');
    // Stale Cash slips: one summary line, not ten "blocked" entries that
    // bury real problems.
    const cashSkipped = (slips || []).filter((s) => s.send_status !== 'sent' && isCashSlip(s));
    const blocked = (slips || [])
      .filter((s) => s.send_status !== 'sent' && !isCashSlip(s) && sendBlockReason(s))
      .map((s) => ({ label: `${s.employees?.name} (${s.employees?.employee_code})`, reason: sendBlockReason(s) }));
    setConfirm({ mode: 'batch', targets, skippedSent, blocked, cashSkipped });
  };

  if (months === null) {
    return <div style={{ ...card, textAlign: 'center', color: 'var(--color-ink-muted)', fontSize: '0.85rem' }}>Loading payslip months…</div>;
  }

  if (!months.length) {
    return (
      <div style={{ ...card, textAlign: 'center' }}>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-ink-secondary)', margin: 0 }}>
          No payslips in the database yet. Import a month on the Upload tab first.
        </p>
      </div>
    );
  }

  const withPdf = (slips || []).filter((s) => s.pdf_path);
  // "Missing" PDFs exclude stale Cash slips — nothing will ever be generated for them.
  const pdfMissing = (slips || []).filter((s) => !s.pdf_path && !isCashSlip(s)).length;
  const sent = (slips || []).filter((s) => s.send_status === 'sent');
  const failed = (slips || []).filter((s) => s.send_status === 'failed');
  const cashSlips = (slips || []).filter((s) => isCashSlip(s) && s.send_status !== 'sent');
  // Stale Cash slips are never dispatched — counting them as "pending" would
  // show a number that can never reach zero.
  const pending = (slips || []).filter((s) => s.send_status !== 'sent' && s.send_status !== 'failed' && !(isCashSlip(s)));
  const warned = (slips || []).filter((s) => s.warnings.length);
  // Month totals include the no-slip rows (blocked + Cash, calculated at
  // upload) — the total is the full payroll cost. Blocked rows with NO
  // figures (pay was not computable at upload) are called out as missing.
  const noSlipBlocked = noSlip.filter((a) => a.status === 'blocked');
  const noSlipCash = noSlip.filter((a) => a.status === 'excluded');
  const noSlipWithPay = noSlip.filter((a) => a.net_pay !== null && a.net_pay !== undefined);
  const noSlipNoPay = noSlip.filter((a) => a.net_pay === null || a.net_pay === undefined);
  const attDeductions = (a) => (a.pf ?? 0) + (a.esic ?? 0) + (a.professional_tax ?? 0) + (a.unpaid_deduction ?? 0);
  const totalGross = (slips || []).reduce((a, s) => a + (s.gross ?? 0), 0) + noSlip.reduce((t, a) => t + (a.gross ?? 0), 0);
  const totalDeductions = (slips || []).reduce((a, s) => a + deductions(s), 0) + noSlip.reduce((t, a) => t + attDeductions(a), 0);
  const hasNet = (slips || []).some((s) => s.net_pay !== null && s.net_pay !== undefined) || noSlipWithPay.length > 0;
  const totalNet = (slips || []).reduce((a, s) => a + (s.net_pay ?? 0), 0) + noSlip.reduce((t, a) => t + (a.net_pay ?? 0), 0);
  const attLabel = (a) => `${a.muster_name || a.muster_employee_id} (${a.muster_employee_id})`;

  return (
    <div>
      {!historyEmp && (
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: (slips && slips.length) ? 16 : 0 }}>
          <div>
            <label style={{ ...statLabel, display: 'block', marginBottom: 6 }}>Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '8px 10px', color: NAVY, fontSize: '0.84rem', fontWeight: 600, outline: 'none' }}
            >
              {months.map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          </div>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => { loadMonths(); loadSlips(month); }} disabled={loading || generating} style={{ marginBottom: 2 }}>
            <RefreshCw size={13} style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true" />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {slips && slips.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {generating && genProgress && (
                <span style={{ fontSize: '0.78rem', color: 'var(--color-ink-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                  Generating {genProgress.done}/{genProgress.total}…
                </span>
              )}
              <button
                type="button"
                className="btn btn-gold btn-sm"
                onClick={() => handleGenerate('missing')}
                disabled={generating || pdfMissing === 0}
              >
                <FileDown size={13} style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true" />
                Generate PDFs ({pdfMissing} missing)
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => handleGenerate('all')} disabled={generating || !!sending}>
                Regenerate all
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={prepareExport} disabled={exportBusy || generating || !!sending} title="Excel: one sheet per payment mode — the transfer instruction for this month">
                <FileSpreadsheet size={13} style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true" />
                {exportBusy && !exportPlan ? 'Reading…' : 'Export by payment mode'}
              </button>
              {sending ? (
                <>
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-ink-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                    Sending {sending.done}/{sending.total}…
                  </span>
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => { stopRef.current = true; }}>
                    Stop
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-navy btn-sm" onClick={openBatchConfirm} disabled={generating}>
                  <Send size={13} style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true" />
                  Send batch…
                </button>
              )}
            </div>
          )}
        </div>

        {slips && slips.length > 0 && (
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            <div><div style={statValue}>{slips.length}</div><div style={statLabel}>Employees</div></div>
            <div><div style={statValue}>{withPdf.length}</div><div style={statLabel}>PDFs generated</div></div>
            <div><div style={statValue}>{sent.length}</div><div style={statLabel}>Sent</div></div>
            <div><div style={{ ...statValue, color: failed.length ? 'var(--color-danger-strong)' : NAVY }}>{failed.length}</div><div style={statLabel}>Failed</div></div>
            <div><div style={statValue}>{pending.length}</div><div style={statLabel}>Pending</div></div>
            {noSlipBlocked.length > 0 && <div><div style={{ ...statValue, color: 'var(--color-warning-strong)' }}>{noSlipBlocked.length}</div><div style={statLabel}>Blocked — no slip</div></div>}
            {noSlipCash.length > 0 && <div><div style={statValue}>{noSlipCash.length}</div><div style={statLabel}>Cash — no slip</div></div>}
            {cashSlips.length > 0 && <div><div style={{ ...statValue, color: 'var(--color-warning-strong)' }}>{cashSlips.length}</div><div style={statLabel}>Stale Cash slips</div></div>}
            <div><div style={{ ...statValue, color: warned.length ? 'var(--color-warning-strong)' : NAVY }}>{warned.length}</div><div style={statLabel}>With warnings</div></div>
            <div><div style={statValue}>{inr(totalGross)}</div><div style={statLabel}>Total gross</div></div>
            <div><div style={statValue}>{inr(totalDeductions)}</div><div style={statLabel}>Total deductions</div></div>
            {hasNet && <div><div style={statValue}>{inr(totalNet)}</div><div style={statLabel}>Total net pay</div></div>}
          </div>
        )}
      </div>
      )}

      {error && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)' }}>
          <p style={{ fontSize: '0.83rem', color: 'var(--color-danger-strong)', margin: 0 }}>{error}</p>
        </div>
      )}

      {sendNotice && (
        <div style={{ ...card, marginBottom: 16 }}>
          <p style={{ fontSize: '0.83rem', color: 'var(--color-ink-secondary)', margin: 0 }}>{sendNotice}</p>
        </div>
      )}

      {sendFailures.length > 0 && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)' }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-danger-strong)', margin: '0 0 6px' }}>
            {sendFailures.length} send{sendFailures.length === 1 ? '' : 's'} failed:
          </p>
          {sendFailures.map((f) => (
            <p key={f.id} style={{ fontSize: '0.78rem', color: 'var(--color-danger-strong)', margin: '2px 0' }}>
              <strong>{f.label}:</strong> {f.error}
            </p>
          ))}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => runSend(sendFailures.map((f) => f.id))}
            disabled={!!sending || generating}
          >
            Retry failed ({sendFailures.length})
          </button>
        </div>
      )}

      {genErrors.length > 0 && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)' }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-danger-strong)', margin: '0 0 6px' }}>
            {genErrors.length} PDF{genErrors.length === 1 ? '' : 's'} failed to generate — the rest completed:
          </p>
          {genErrors.map((e, i) => (
            <p key={i} style={{ fontSize: '0.78rem', color: 'var(--color-danger-strong)', margin: '2px 0' }}>{e}</p>
          ))}
        </div>
      )}

      {!historyEmp && warned.length > 0 && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-warning-border)', background: 'var(--color-warning-bg)' }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-ink)', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={15} aria-hidden="true" /> Warnings — recomputed from stored data
          </p>
          {warned.map((s) => (
            <p key={s.id} style={{ fontSize: '0.78rem', color: 'var(--color-ink)', margin: '2px 0', lineHeight: 1.5 }}>
              <strong>{s.employees?.name} ({s.employees?.employee_code}):</strong> {s.warnings.join(' ')}
            </p>
          ))}
        </div>
      )}

      {!historyEmp && noSlipError && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)' }}>
          <p style={{ fontSize: '0.83rem', color: 'var(--color-danger-strong)', margin: 0, lineHeight: 1.5 }}>{noSlipError}</p>
        </div>
      )}

      {!historyEmp && noSlip.length > 0 && (
        <div style={{ ...card, marginBottom: 16, borderColor: noSlipBlocked.length ? 'var(--color-warning-border)' : 'var(--color-border)' }}>
          <p style={{ fontSize: '0.85rem', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
            Calculated — no slip ({noSlip.length}{noSlipBlocked.length && noSlipCash.length ? `: ${noSlipBlocked.length} blocked, ${noSlipCash.length} Cash` : ''})
          </p>
          <p style={{ fontSize: '0.76rem', color: 'var(--color-ink-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Their figures are in the month totals above; no PDF is generated and nothing is ever sent.
            {noSlipBlocked.length > 0 && ' Blocked rows (joiner/leaver, sheet mismatch, …) get a slip once the muster or the employee record is fixed and the month re-uploaded.'}
            {noSlipCash.length > 0 && ' Cash rows are excluded by payment type, not by a data problem — nothing to fix.'}
          </p>
          {/* Numbered on its own (1..n), separately from the month table: this
              card lists people WITHOUT a document, the table lists slips —
              one sequence across two different blocks would mislead. */}
          {noSlip.map((a, i) => (
            <div key={a.id} style={{ marginBottom: 8 }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 700, color: NAVY, margin: '0 0 2px' }}>
                <span style={{ display: 'inline-block', minWidth: 22, color: 'var(--color-ink-muted)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{i + 1}.</span>
                {a.status === 'excluded'
                  ? <span className="badge badge-grey" style={{ marginRight: 8 }} title="Payment type Cash — no payslip by design">Cash</span>
                  : <span className="badge badge-amber" style={{ marginRight: 8 }} title="Blocked at upload — fix and re-upload to issue a slip">Blocked</span>}
                {attLabel(a)}
                {a.net_pay !== null && a.net_pay !== undefined ? (
                  <span style={{ fontWeight: 600, color: 'var(--color-ink-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                    {' '}— {a.days_paid} paid / {a.days_unpaid} unpaid / {a.dash_days} not employed · gross earned {inr(a.gross_earned)} · net {inr(a.net_pay)}
                  </span>
                ) : (
                  <span style={{ fontWeight: 700, color: 'var(--color-warning-strong)' }}> — no figures: MISSING from the month totals</span>
                )}
              </p>
              {(a.flags || []).filter((f) => f.level === 'blocker').map((f, i) => (
                <p key={i} style={{ fontSize: '0.76rem', color: 'var(--color-ink-secondary)', margin: '0 0 2px 12px', lineHeight: 1.5 }}>{f.message}</p>
              ))}
            </div>
          ))}
          {noSlipNoPay.length > 0 && (
            <p style={{ fontSize: '0.76rem', color: 'var(--color-warning-strong)', fontWeight: 600, margin: '8px 0 0' }}>
              {noSlipNoPay.length} of these {noSlipNoPay.length === 1 ? 'has' : 'have'} no calculable figures — the totals above are incomplete until resolved.
            </p>
          )}
        </div>
      )}

      {!historyEmp && slips && slips.length > 0 && (
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'right' }} title="Row number as displayed">Sl No</th>
                <th style={{ textAlign: 'left' }}>Code</th>
                <th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>Department</th>
                <th style={{ textAlign: 'left' }}>Phone</th>
                <th style={{ textAlign: 'right' }} title="Days paid / days unpaid, counted from the muster">Days P / U</th>
                <th style={{ textAlign: 'right' }}>Gross</th>
                <th style={{ textAlign: 'right' }}>Deductions</th>
                <th style={{ textAlign: 'right' }}>Net Pay</th>
                <th style={{ textAlign: 'left' }}>PDF</th>
                <th style={{ textAlign: 'left' }}>Sent</th>
              </tr>
            </thead>
            <tbody>
              {slips.map((s, i) => (
                <tr key={s.id}>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-ink-muted)' }}>{i + 1}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{s.employees?.employee_code}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => openHistory(s.employees)}
                      title="View all months for this employee"
                      style={{ background: 'none', border: 'none', padding: 0, color: NAVY, fontSize: 'inherit', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--color-border-strong)', textUnderlineOffset: 3 }}
                    >
                      {s.employees?.name}
                    </button>
                    {s.warnings.length > 0 && (
                      <AlertTriangle size={13} style={{ color: 'var(--color-warning-strong)', marginLeft: 6, verticalAlign: -2 }} aria-label="Has warnings" />
                    )}
                  </td>
                  <td>{s.employees?.department || '—'}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{s.employees?.phone || '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: s.days_unpaid ? 'var(--color-danger)' : undefined }}>{daysCell(s)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.gross)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(deductions(s))}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{inr(s.net_pay)}</td>
                  <td>{pdfCell(s)}</td>
                  <td>{sentCell(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {historyEmp && (
        <>
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => { setHistoryEmp(null); setHistorySlips(null); }}
                disabled={!!sending}
              >
                ← Back to {fmtMonth(month)}
              </button>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: NAVY }}>
                  {historyEmp.name} <span style={{ fontWeight: 600, color: 'var(--color-ink-muted)' }}>({historyEmp.employee_code})</span>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {historyEmp.department || '—'} · {historyEmp.phone || 'no phone'} · UAN {historyEmp.uan || 'NA'}
                  {historySlips ? ` · ${historySlips.length} payslip${historySlips.length === 1 ? '' : 's'} on record` : ''}
                </div>
              </div>
              {sending && (
                <span style={{ fontSize: '0.78rem', color: 'var(--color-ink-secondary)', marginLeft: 'auto' }}>Sending…</span>
              )}
            </div>
          </div>

          {historySlips === null && (
            <div style={{ ...card, textAlign: 'center', color: 'var(--color-ink-muted)', fontSize: '0.85rem' }}>Loading history…</div>
          )}

          {historySlips && historySlips.length === 0 && (
            <div style={{ ...card, textAlign: 'center' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-ink-secondary)', margin: 0 }}>No payslips on record for this employee.</p>
            </div>
          )}

          {historySlips && historySlips.length > 0 && (
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Month</th>
                    <th style={{ textAlign: 'right' }} title="Days paid / days unpaid, counted from the muster">Days P / U</th>
                    <th style={{ textAlign: 'right' }}>Basic</th>
                    <th style={{ textAlign: 'right' }}>DA</th>
                    <th style={{ textAlign: 'right' }}>Residual</th>
                    <th style={{ textAlign: 'right' }}>Gross</th>
                    <th style={{ textAlign: 'right' }}>PF</th>
                    <th style={{ textAlign: 'right' }}>ESIC</th>
                    <th style={{ textAlign: 'right' }}>Prof. Tax</th>
                    <th style={{ textAlign: 'right' }}>Leave Ded.</th>
                    <th style={{ textAlign: 'right' }}>Deductions</th>
                    <th style={{ textAlign: 'right' }}>Net Pay</th>
                    <th style={{ textAlign: 'left' }}>PDF</th>
                    <th style={{ textAlign: 'left' }}>Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {historySlips.map((s) => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>
                        {fmtMonth(s.month)}
                        {s.warnings.length > 0 && (
                          <AlertTriangle size={13} style={{ color: 'var(--color-warning-strong)', marginLeft: 6, verticalAlign: -2 }} aria-label={s.warnings.join(' ')} title={s.warnings.join(' ')} />
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: s.days_unpaid ? 'var(--color-danger)' : undefined }}>{daysCell(s)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.basic)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.da)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.residual_pay)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{inr(s.gross)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.pf)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.esic)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.professional_tax)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{inr(s.lwp_deduction)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{inr(deductions(s))}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{inr(s.net_pay)}</td>
                      <td>{pdfCell(s)}</td>
                      <td>{sentCell(s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {exportPlan && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={() => { if (!exportBusy) setExportPlan(null); }}
        >
          <div style={{ ...card, maxWidth: 560, width: '100%', background: '#ffffff', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            {exportPlan.error ? (
              <>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--color-danger-strong)', margin: '0 0 10px' }}>Export by payment mode</h3>
                <p style={{ fontSize: '0.84rem', color: 'var(--color-danger-strong)', margin: '0 0 14px', lineHeight: 1.6 }}>{exportPlan.error}</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setExportPlan(null)}>Close</button>
                </div>
              </>
            ) : (() => {
              const { plan } = exportPlan;
              const blockedOnSheets = plan.attention.filter((a) => a.kind === 'blocked');
              // Missing IFSCs are counted, not listed one by one: until HR has
              // typed them in there can be dozens, and the names are on the
              // attention sheet and red in the bank sheets.
              const noIfsc = plan.attention.filter((a) => a.kind === 'no_ifsc');
              const others = plan.attention.filter((a) => a.kind !== 'blocked' && a.kind !== 'no_ifsc');
              return (
                <>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: NAVY, margin: '0 0 6px' }}>Export salary by payment mode</h3>
                  {/* The month and the total, stated plainly: this is a transfer
                      instruction, and a wrong month must be obvious HERE. */}
                  <p style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, margin: '0 0 4px', fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}>
                    {plan.monthLabel} — {plan.placed} employee{plan.placed === 1 ? '' : 's'}, {rupees(plan.total)}
                  </p>
                  <p style={{ fontSize: '0.76rem', color: 'var(--color-ink-muted)', margin: '0 0 12px' }}>
                    Net pay as stored for the month, across the three sheets. File: {plan.filename}
                  </p>
                  <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse', marginBottom: 12 }}>
                    <tbody>
                      {PAYMENT_MODES.map((m) => (
                        <tr key={m} style={{ borderTop: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '5px 0', color: 'var(--color-ink)', fontWeight: 600 }}>{m}{m === 'Cash' ? <span style={{ fontWeight: 400, color: 'var(--color-ink-muted)' }}> — no account or IFSC column</span> : ''}</td>
                          <td style={{ padding: '5px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-ink-secondary)' }}>{plan.perMode[m].count} employee{plan.perMode[m].count === 1 ? '' : 's'}</td>
                          <td style={{ padding: '5px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: NAVY, paddingLeft: 16 }}>{rupees(plan.perMode[m].total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!plan.complete && (
                    <div style={{ background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-danger-strong)', margin: '0 0 4px' }}>
                        Incomplete — {plan.missing.length} {plan.missing.length === 1 ? 'person is' : 'people are'} in NO sheet total:
                      </p>
                      {plan.missing.map((a, i) => (
                        <p key={i} style={{ fontSize: '0.78rem', color: 'var(--color-ink)', margin: '2px 0', lineHeight: 1.5 }}><strong>{a.person.name} ({a.person.code}):</strong> {a.issue}</p>
                      ))}
                    </div>
                  )}
                  {blockedOnSheets.length > 0 && (
                    <div style={{ background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
                        {blockedOnSheets.length} blocked at upload — included on their sheet (shaded) and listed on "{ATTENTION_SHEET}":
                      </p>
                      {blockedOnSheets.map((a, i) => (
                        <p key={i} style={{ fontSize: '0.78rem', color: 'var(--color-ink)', margin: '2px 0', lineHeight: 1.5, fontVariantNumeric: 'tabular-nums' }}>
                          <strong>{a.person.name} ({a.person.code}):</strong> {a.mode}, {rupees(a.person.net_pay)}
                        </p>
                      ))}
                    </div>
                  )}
                  {others.filter((a) => a.kind !== 'no_figure' && a.kind !== 'unplaceable').length > 0 && (
                    <div style={{ background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Also on "{ATTENTION_SHEET}":</p>
                      {others.filter((a) => a.kind !== 'no_figure' && a.kind !== 'unplaceable').map((a, i) => (
                        <p key={i} style={{ fontSize: '0.78rem', color: 'var(--color-ink)', margin: '2px 0', lineHeight: 1.5 }}><strong>{a.person.name} ({a.person.code}):</strong> {a.issue}</p>
                      ))}
                    </div>
                  )}
                  {noIfsc.length > 0 && (
                    <div style={{ background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
                        {noIfsc.length} bank row{noIfsc.length === 1 ? ' has' : 's have'} no IFSC — the cell is red on the sheet and each is listed on "{ATTENTION_SHEET}".
                      </p>
                      <p style={{ fontSize: '0.78rem', color: 'var(--color-ink)', margin: 0, lineHeight: 1.5 }}>
                        The bank cannot act on those rows as they are. Add the IFSC codes in the Employees tab and export again.
                      </p>
                    </div>
                  )}
                  {plan.attention.length === 0 && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-success-strong)', margin: '0 0 10px' }}>Nothing to check — everyone is placed with a figure, an account number and an IFSC.</p>
                  )}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setExportPlan(null)} disabled={exportBusy}>Cancel</button>
                    <button type="button" className="btn btn-navy btn-sm" onClick={downloadExport} disabled={exportBusy}>
                      <FileDown size={13} style={{ marginRight: 5, verticalAlign: -2 }} aria-hidden="true" />
                      {exportBusy ? 'Writing…' : `Download ${plan.monthLabel}`}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {confirm && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={() => setConfirm(null)}
        >
          <div style={{ ...card, maxWidth: 460, width: '100%', background: '#ffffff' }} onClick={(e) => e.stopPropagation()}>
            {confirm.mode === 'single' ? (
              <>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>
                  {confirm.slip.send_status === 'sent' ? 'Re-send salary slip' : 'Send salary slip'}
                </h3>
                {confirm.slip.send_status === 'sent' && (
                  <p style={{ fontSize: '0.82rem', color: 'var(--color-warning-strong)', margin: '0 0 8px', fontWeight: 600 }}>
                    Already sent {fmtDate(confirm.slip.sent_at)} — this sends it again.
                  </p>
                )}
                <p style={{ fontSize: '0.84rem', color: 'var(--color-ink-secondary)', margin: '0 0 14px', lineHeight: 1.6 }}>
                  {confirm.slip.employees?.name} ({confirm.slip.employees?.employee_code}) will receive their salary slip
                  for <strong>{fmtMonth(confirm.slip.month)}</strong> on WhatsApp at <strong>{confirm.slip.employees?.phone}</strong>.
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setConfirm(null)}>Cancel</button>
                  <button type="button" className="btn btn-navy btn-sm" onClick={handleConfirmSend}>
                    {confirm.slip.send_status === 'sent' ? 'Re-send now' : 'Send now'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>Send salary slips</h3>
                <p style={{ fontSize: '1.02rem', fontWeight: 700, color: NAVY, margin: '0 0 12px', lineHeight: 1.5 }}>
                  {confirm.targets.length} {confirm.targets.length === 1 ? 'person' : 'people'} will receive their salary slip
                  for {fmtMonth(month)} on WhatsApp.
                </p>
                {confirm.skippedSent.length > 0 && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-secondary)', margin: '0 0 6px' }}>
                    {confirm.skippedSent.length} already sent — skipped (use the per-row Re-send if needed).
                  </p>
                )}
                {(confirm.cashSkipped?.length || 0) > 0 && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-secondary)', margin: '0 0 6px' }}>
                    {confirm.cashSkipped.length} stale Cash slip{confirm.cashSkipped.length === 1 ? '' : 's'} — skipped; re-upload the month to remove {confirm.cashSkipped.length === 1 ? 'it' : 'them'}.
                  </p>
                )}
                {confirm.blocked.length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-warning-strong)', margin: '0 0 6px' }}>
                    {confirm.blocked.length} cannot be sent:
                    {confirm.blocked.map((b, i) => (
                      <p key={i} style={{ margin: '2px 0 0', color: 'var(--color-ink-secondary)' }}><strong>{b.label}:</strong> {b.reason}</p>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setConfirm(null)}>Cancel</button>
                  <button type="button" className="btn btn-navy btn-sm" onClick={handleConfirmSend} disabled={!confirm.targets.length}>
                    Send to {confirm.targets.length} {confirm.targets.length === 1 ? 'person' : 'people'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
